import { t } from "i18next"
import { z } from "zod"
import { CASE_REFERENCE_MAX_LENGTH, textAreaDefaultMaxLength } from "@/models/common"
import { Law, NotBillableReason, TreatmentReason } from "@/types/__generated__/union-types"

const IVG_CASE_REFERENCE_REGEX = /^(\d{6}|\d{9}|\d{10}|\d{14})$/

const createChEditBillingSchema = () =>
  z
    .object({
      isNotBillable: z.boolean(),
      reason: z.string().optional(),
      treatmentReason: z.custom<TreatmentReason>().optional(),
      notBillableReason: z.custom<NotBillableReason>(),
      dateOfAccident: z.date().optional(),
      accidentReference: z.string().optional(),
      organisationId: z.string().optional(),
      lawType: z.custom<Law>().optional(),
      ivgCaseReference: z.string().max(24).optional(),
      mvgCaseReference: z.string().optional(),
    })
    .superRefine(
      (
        {
          isNotBillable,
          reason,
          treatmentReason,
          notBillableReason,
          dateOfAccident,
          organisationId,
          lawType,
          ivgCaseReference,
          mvgCaseReference,
          accidentReference,
        },
        ctx,
      ) => {
        if (isNotBillable && notBillableReason === "OTHER_REASON") {
          if (!reason) {
            ctx.addIssue({
              code: "custom",
              path: ["reason"],
              message: t("common.fieldErrorList.requiredField"),
            })
          } else if (reason.length > textAreaDefaultMaxLength) {
            ctx.addIssue({
              code: "custom",
              path: ["reason"],
              message: t("common.fieldErrorList.characterLimit"),
            })
          }
        }

        if (!isNotBillable) {
          if (!treatmentReason)
            ctx.addIssue({
              code: "custom",
              path: ["treatmentReason"],
              message: t("common.fieldErrorList.requiredField"),
            })

          if (!lawType) {
            ctx.addIssue({
              code: "custom",
              path: ["lawType"],
              message: t("common.fieldErrorList.requiredField"),
            })
          }

          if (lawType === "UVG") {
            if (!dateOfAccident) {
              ctx.addIssue({
                code: "custom",
                path: ["dateOfAccident"],
                message: t("common.fieldErrorList.requiredField"),
              })
            }

            if (!organisationId) {
              ctx.addIssue({
                code: "custom",
                path: ["organisationId"],
                message: t("common.fieldErrorList.requiredField"),
              })
            }
          }

          if (lawType === "IVG") {
            if (!ivgCaseReference) {
              ctx.addIssue({
                code: "custom",
                path: ["ivgCaseReference"],
                message: t("common.fieldErrorList.requiredField"),
              })
            } else if (!IVG_CASE_REFERENCE_REGEX.test(ivgCaseReference)) {
              ctx.addIssue({
                code: "custom",
                path: ["ivgCaseReference"],
                message: t("common.fieldErrorList.caseReferenceFormat"),
              })
            }
          }

          if (lawType === "MVG") {
            if (!mvgCaseReference) {
              ctx.addIssue({
                code: "custom",
                path: ["mvgCaseReference"],
                message: t("common.fieldErrorList.requiredField"),
              })
            } else if (mvgCaseReference.length > CASE_REFERENCE_MAX_LENGTH) {
              ctx.addIssue({
                code: "custom",
                path: ["mvgCaseReference"],
                message: t("common.fieldErrorList.maxLengthField", { max: CASE_REFERENCE_MAX_LENGTH }),
              })
            }
          }

          if (lawType === "UVG" && accidentReference && accidentReference.length > CASE_REFERENCE_MAX_LENGTH) {
            ctx.addIssue({
              code: "custom",
              path: ["accidentReference"],
              message: t("common.fieldErrorList.accidentReferenceMaxLength", {
                maxLength: CASE_REFERENCE_MAX_LENGTH,
              }),
            })
          }
        }
      },
    )

type ChEditBillingSchemaType = z.infer<ReturnType<typeof createChEditBillingSchema>>

export { createChEditBillingSchema, type ChEditBillingSchemaType }
